"use client"

// Sidebar root container. Owns the sidebar-width / sidebar-translate
// CSS-variable side effect that the rest of the layout positions
// off. Sibling files own the layout / menu / control primitives —
// see SidebarLayout, SidebarMenu, SidebarControls.

import * as React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { cn } from '../../../../src/cn'
import { useSidebar } from './use-sidebar'
import { sidebarVariants } from './Sidebar.variants'

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & VariantProps<typeof sidebarVariants>
>(({ className, variant, ...props }, ref) => {
  const { state, isMobile } = useSidebar()

  React.useEffect(() => {
    const value = isMobile
      ? 'var(--sidebar-width-mobile)'
      : state === 'expanded'
        ? 'var(--sidebar-width-expanded)'
        : '0px'
    document.documentElement.style.setProperty('--sidebar-width', value)
    const translateValue = state === 'expanded' ? '0px' : '-100%'
    document.documentElement.style.setProperty('--sidebar-translate', translateValue)
  }, [state, isMobile])

  return (
    <div
      ref={ref}
      className={cn(
        sidebarVariants({ variant }),
        'overflow-y-auto overflow-x-hidden p-4',
        state === 'collapsed' && 'hidden',
        state === 'expanded' ? 'w-64 max-w-64 min-w-64' : 'w-0 max-w-0 min-w-0',
        'shrink-0 grow-0',
        className,
      )}
      {...props}
    />
  )
})

SidebarContent.displayName = 'SidebarContent'

export { SidebarContent }
